/**
 * O que faz: Gerencia campanhas, templates e execuções do sistema de follow-up.
 * Onde usa: Configurações de follow-up, wizard de reativação, chat e cards de campanha.
 * Conexões: Supabase client, React Query, contexto de clínica e notificações toast.
 * Tabelas: follow_up_campaigns, follow_up_templates, follow_up_execucoes e leads.
 * Autor: IA
 * Data: 23/04/2026
 */

import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';
import { useAdAliases } from '@/hooks/useAdAliases';
import { toast } from 'sonner';
import type {
  FollowupCampaign,
  FollowupCampaignDraft,
  FollowupExecution,
  FollowupTemplate,
} from '@/types/followup';

export type { FollowupCampaign, FollowupExecution, FollowupTemplate };

export interface FollowupAdOption {
  originalName: string;
  displayName: string;
  alias: string | null;
}

export interface FollowupImageUploadResult {
  objectKey: string;
  bucket: string;
  signedUrl: string;
  signedUrlExpiresInSeconds: number;
  mimeType: string;
  fileName: string;
  sizeBytes: number;
}

const extractFunctionErrorMessage = async (error: unknown): Promise<string> => {
  const fallbackMessage = error instanceof Error ? error.message : 'Erro desconhecido';
  const response = (error as { context?: Response | null })?.context;

  if (!response || typeof response.clone !== 'function') return fallbackMessage;

  const responseClone = response.clone();
  const contentType = response.headers.get('content-type') || '';

  // Le o corpo retornado pela Edge Function para mostrar a causa real do 500.
  // Motivo: o supabase-js coloca a Response em error.context e a mensagem padrao
  // "non-2xx" nao informa se a falha foi bucket, endpoint ou permissao.
  if (contentType.includes('application/json')) {
    const payload = await responseClone.json().catch(() => null) as {
      error?: string;
      message?: string;
      code?: string;
    } | null;

    const details = [payload?.error, payload?.message, payload?.code]
      .filter(Boolean)
      .join(' | ');

    if (details) return details;
  }

  const text = await responseClone.text().catch(() => '');
  return text.trim() || fallbackMessage;
};

export const useFollowupCampaigns = (enabled = true, clinicaId?: string | null) => {
  return useQuery({
    // clinicaId entra na chave para o admin alternar entre clínicas sem reaproveitar cache da conta anterior.
    queryKey: ['followup-campaigns', clinicaId ?? 'self'],
    enabled,
    queryFn: async (): Promise<FollowupCampaign[]> => {
      // Tabela: follow_up_campaigns
      // Operação: SELECT
      // Campos afetados: nenhum; lista campanhas da clínica visíveis por RLS.
      // Filtros: is_preset=false para esconder presets globais; clinica_id quando o admin escolhe uma conta específica.
      // Riscos: mostra configuração de automações; RLS limita à clínica do usuário e o filtro evita misturar contas no modo admin.
      let query = (supabase as any)
        .from('follow_up_campaigns')
        .select('*, follow_up_templates(*)')
        .eq('is_preset', false)
        // Tabela: follow_up_campaigns; Operação: SELECT; Filtro: somente campanhas não arquivadas.
        // Risco: evita que configurações retiradas pela API reapareçam e sejam reativadas pela interface.
        .is('archived_at', null);

      // Admin envia clinicaId para isolar a conta visualizada; usuário comum mantém apenas o recorte do RLS.
      if (clinicaId) {
        query = query.eq('clinica_id', clinicaId);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        throw new Error(`Erro ao buscar campanhas de follow-up: ${error.message}`);
      }

      return (data ?? []) as FollowupCampaign[];
    },
    staleTime: 30 * 1000,
  });
};

export const useFollowupTemplates = (campaignId: string | null) => {
  return useQuery({
    queryKey: ['followup-templates', campaignId],
    enabled: !!campaignId,
    queryFn: async (): Promise<FollowupTemplate[]> => {
      if (!campaignId) return [];

      // Tabela: follow_up_templates
      // Operação: SELECT
      // Campos afetados: nenhum; lê sequência de mensagens da campanha.
      // Filtros: campaign_id recebido da UI.
      // Riscos: conteúdo de mensagens automáticas; RLS limita a campanhas da clínica.
      const { data, error } = await (supabase as any)
        .from('follow_up_templates')
        .select('*')
        .eq('campaign_id', campaignId)
        // Tabela: follow_up_templates; Operação: SELECT; Filtro: somente etapas não arquivadas.
        // Risco: preserva o histórico no banco sem reutilizar mensagem removida em novos envios.
        .is('archived_at', null)
        .order('sequencia', { ascending: true });

      if (error) {
        throw new Error(`Erro ao buscar templates: ${error.message}`);
      }

      return (data ?? []) as FollowupTemplate[];
    },
    staleTime: 30 * 1000,
  });
};

export const useFollowupAdOptions = (enabled = true) => {
  const { getAliasForAd, aliases, effectiveClinicaId } = useAdAliases();

  const adNamesQuery = useQuery({
    queryKey: ['followup-ad-options', effectiveClinicaId],
    enabled: enabled && !!effectiveClinicaId,
    queryFn: async (): Promise<string[]> => {
      // Tabela: leads
      // Operacao: SELECT
      // Campos afetados: nenhum; le apenas ad_name para montar a lista de anuncios disponiveis no modal.
      // Filtros: somente leads com ad_name preenchido e ordenados alfabeticamente para facilitar busca visual.
      // Riscos: expoe nomes comerciais dos anuncios apenas dentro da clinica autenticada via RLS.
      const { data, error } = await (supabase as any)
        .from('leads')
        .select('ad_name')
        .not('ad_name', 'is', null)
        .order('ad_name', { ascending: true });

      if (error) {
        throw new Error(`Erro ao buscar anuncios para o follow-up: ${error.message}`);
      }

      // Normaliza espacos e remove duplicidade porque a mesma campanha pode ter varios leads.
      // Motivo: o modal precisa listar anuncios unicos para o usuario escolher sem ruido.
      const uniqueNames = Array.from(new Set<string>(
        (data ?? [])
          .map((row: { ad_name: string | null }) => String(row.ad_name ?? '').trim())
          .filter(Boolean),
      ));

      return uniqueNames;
    },
    staleTime: 60 * 1000,
  });

  const adOptions = useMemo<FollowupAdOption[]>(() => {
    // Recalcula a exibicao quando os apelidos terminam de carregar.
    // Motivo: leads e ad_aliases chegam em queries separadas; sem isso o modal ficava preso no nome original.
    return (adNamesQuery.data ?? [])
      .map((originalName) => {
        const alias = getAliasForAd(originalName);
        return {
          originalName,
          alias,
          displayName: alias ? `${alias} (${originalName})` : originalName,
        };
      })
      .sort((firstOption, secondOption) => firstOption.displayName.localeCompare(secondOption.displayName, 'pt-BR'));
  }, [adNamesQuery.data, aliases, getAliasForAd]);

  return {
    ...adNamesQuery,
    data: adOptions,
  };
};

export const useUploadFollowupImage = () => {
  const { clinicaId } = useClinica();

  return useMutation({
    mutationFn: async (file: File): Promise<FollowupImageUploadResult> => {
      // Valida o arquivo no cliente antes de chamar a Edge Function.
      // Motivo: evita upload desnecessario e mostra erro em portugues imediatamente.
      if (!file.type.startsWith('image/')) {
        throw new Error('Envie apenas arquivos de imagem.');
      }
      if (file.size > 8 * 1024 * 1024) {
        throw new Error('A imagem precisa ter no maximo 8MB.');
      }

      const formData = new FormData();
      formData.append('file', file);
      if (clinicaId) formData.append('clinicId', clinicaId);

      // Edge Function: upload-followup-image
      // Operacao: POST multipart
      // Campos afetados: nenhum no banco; salva objeto no MinIO e retorna chave persistente.
      // Filtros: clinicId vem do contexto autenticado e e validado pela funcao.
      // Riscos: manipula midia de campanha; a URL assinada expira e nao deve ser usada como chave permanente.
      const { data, error } = await supabase.functions.invoke('upload-followup-image', {
        body: formData,
      });

      if (error) {
        const details = await extractFunctionErrorMessage(error);
        throw new Error(`Erro ao enviar imagem: ${details}`);
      }

      const attachment = (data as any)?.attachment;
      if (!attachment?.objectKey || !attachment?.bucket || !attachment?.signedUrl) {
        throw new Error('Upload concluido, mas a resposta da imagem veio incompleta.');
      }

      return {
        objectKey: attachment.objectKey,
        bucket: attachment.bucket,
        signedUrl: attachment.signedUrl,
        signedUrlExpiresInSeconds: Number(attachment.signedUrlExpiresInSeconds || 0),
        mimeType: attachment.mimeType || file.type,
        fileName: attachment.fileName || file.name,
        sizeBytes: Number(attachment.sizeBytes || file.size),
      };
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useCreateFollowupCampaign = (overrideClinicaId?: string | null) => {
  const queryClient = useQueryClient();
  const { clinicaId } = useClinica();
  // Admin passa overrideClinicaId para criar sob a clínica selecionada; usuário comum usa o próprio clinicaId.
  const effectiveClinicaId = overrideClinicaId || clinicaId;

  return useMutation({
    mutationFn: async (draft: FollowupCampaignDraft): Promise<FollowupCampaign> => {
      // Valida a clínica antes do insert porque campanhas operacionais precisam pertencer
      // a uma clínica real para RLS, envio e auditoria funcionarem corretamente.
      // Retorno: erro amigável se a sessão ainda não carregou a clínica.
      if (!effectiveClinicaId) {
        throw new Error('Clínica não encontrada. Recarregue a página e tente novamente.');
      }

      // Bloqueia campanha sem mensagem para evitar automação ativa sem conteúdo enviado.
      // Motivo: uma campanha precisa de ao menos uma etapa editável.
      // Retorno: erro em português antes de qualquer insert no banco.
      if (draft.templates.length === 0 || draft.templates.every((template) => !template.conteudo.trim() && !template.gerado_por_ia && !template.imagem_object_key)) {
        throw new Error('Adicione texto, IA ou imagem em pelo menos uma etapa antes de salvar.');
      }

      const janelaDiasCompatibilidade = Math.max(1, Math.ceil(draft.janela_inatividade_minutos / 1440));
      const nowIso = new Date().toISOString();

      // Tabela: follow_up_campaigns
      // Operação: INSERT
      // Campos afetados: clinica_id, nome, descricao, tipo, gatilho, timing,
      // janela_maxima_inatividade_minutos, regras_atualizadas_em, cooldown e metadados de IA.
      // Filtros: não há where em insert; clinica_id vem do contexto autenticado.
      // Riscos: cria automação de mensagens; começa inativa para revisão antes do disparo.
      const { data: campaign, error: campaignError } = await (supabase as any)
        .from('follow_up_campaigns')
        .insert({
          clinica_id: effectiveClinicaId,
          nome: draft.nome.trim(),
          descricao: draft.descricao.trim() || null,
          tipo: draft.tipo,
          ativo: false,
          dias_inatividade: janelaDiasCompatibilidade,
          janela_inatividade_minutos: draft.janela_inatividade_minutos,
          janela_maxima_inatividade_minutos: draft.janela_maxima_inatividade_minutos,
          horario_envio: draft.horario_envio,
          horario_limite_envio: draft.horario_limite_envio,
          modo_envio: draft.modo_envio,
          apenas_dias_uteis: draft.apenas_dias_uteis,
          fuso_horario: 'America/Sao_Paulo',
          tipo_gatilho: draft.tipo_gatilho,
          padrao_gatilho_texto: draft.padrao_gatilho_texto.trim() || null,
          padrao_match_type: draft.padrao_match_type,
          usa_ia: draft.usa_ia,
          ia_prompt_base: draft.ia_prompt_base.trim() || null,
          auto_pausar_ao_responder: draft.auto_pausar_ao_responder,
          apenas_leads_anuncio: draft.apenas_leads_anuncio,
          ad_name_filtro: draft.apenas_leads_anuncio ? (draft.ad_name_filtro.trim() || null) : null,
          apenas_link_personalizado: draft.apenas_link_personalizado,
          // Mantém null quando a segmentação está desligada para preservar campanhas sem filtro.
          // Motivo: o processor só restringe o público quando recebe um identificador de etapa válido.
          etapa_kanban_id_filtro: draft.filtrar_por_etapa_kanban
            ? (draft.etapa_kanban_id_filtro || null)
            : null,
          // Público bloqueado: o processor confere na criação e de novo na hora do envio.
          etapas_bloqueadas_ids: draft.etapas_bloqueadas_ids,
          ignorar_leads_com_venda: draft.ignorar_leads_com_venda,
          cooldown_dias_relancamento: draft.cooldown_dias_relancamento,
          limite_diario: draft.limite_diario,
          usar_classificacao_inteligente: draft.usar_classificacao_inteligente,
          frases_sem_followup: draft.frases_sem_followup
            .split('\n')
            .map((frase) => frase.trim())
            .filter(Boolean),
          regras_atualizadas_em: nowIso,
          is_preset: false,
        })
        .select()
        .single();

      if (campaignError) {
        throw new Error(`Erro ao criar campanha: ${campaignError.message}`);
      }

      const templates = draft.templates
        .filter((template) => template.conteudo.trim() || template.gerado_por_ia || template.imagem_object_key)
        .map((template, index) => ({
          campaign_id: campaign.id,
          sequencia: index + 1,
          titulo: template.titulo.trim() || `Mensagem ${index + 1}`,
          conteudo: template.conteudo.trim() || 'Mensagem gerada por IA no momento do envio.',
          tipo_mensagem: template.imagem_object_key ? 'image' : 'text',
          intervalo_dias: index === 0 ? 0 : Math.max(1, Math.ceil(template.intervalo_minutos / 1440)),
          intervalo_minutos: index === 0 ? 0 : template.intervalo_minutos,
          ativo: true,
          gerado_por_ia: Boolean(template.gerado_por_ia),
          // Mantém a coluna legada vazia porque o modelo efetivo agora vem da configuração global de follow-up.
          // Motivo: a campanha não deve carregar uma escolha de modelo feita no modal para substituir a regra administrativa.
          ia_modelo: null,
          imagem_object_key: template.imagem_object_key || null,
          imagem_bucket: template.imagem_bucket || null,
          imagem_mimetype: template.imagem_mimetype || null,
          imagem_nome_arquivo: template.imagem_nome_arquivo || null,
          template_oficial_id: template.template_oficial_id || null,
          mapeamento_variaveis: template.mapeamento_variaveis ?? [],
        }));

      // Tabela: follow_up_templates
      // Operação: INSERT
      // Campos afetados: campaign_id, sequencia, titulo, conteudo, intervalo e metadados de IA.
      // Filtros: não há where em insert; campaign_id foi criado acima.
      // Riscos: grava mensagens que podem ser enviadas a leads; campanha permanece inativa por padrão.
      const { error: templatesError } = await (supabase as any)
        .from('follow_up_templates')
        .insert(templates);

      if (templatesError) {
        // Remove a campanha recém-criada para evitar configuração sem mensagens.
        // Motivo: sem transação no cliente, esta compensação mantém o banco consistente
        // caso o insert dos templates falhe depois do insert da campanha.
        await (supabase as any)
          .from('follow_up_campaigns')
          .delete()
          .eq('id', campaign.id);
        throw new Error(`Campanha criada, mas houve erro ao salvar mensagens: ${templatesError.message}`);
      }

      return campaign as FollowupCampaign;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followup-campaigns'] });
      toast.success('Campanha criada. Revise e ative quando estiver pronta.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateFollowupCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, draft }: { id: string; draft: FollowupCampaignDraft }): Promise<void> => {
      // Bloqueia campanha sem mensagem para evitar salvar automação sem etapa válida.
      // Motivo: sem texto fixo ou IA ativa, o fluxo fica configurado mas não envia nada útil.
      if (draft.templates.length === 0 || draft.templates.every((template) => !template.conteudo.trim() && !template.gerado_por_ia && !template.imagem_object_key)) {
        throw new Error('Adicione texto, IA ou imagem em pelo menos uma etapa antes de salvar.');
      }

      const janelaDiasCompatibilidade = Math.max(1, Math.ceil(draft.janela_inatividade_minutos / 1440));
      const nowIso = new Date().toISOString();

      // Tabela: follow_up_campaigns
      // Operação: UPDATE
      // Campos afetados: nome, descrição, gatilho, timing, janela maxima, IA e limites operacionais.
      // Filtros: id da campanha; RLS garante escopo da clínica do usuário.
      // Riscos: altera regra de disparo em produção; manter validações evita config inválida.
      const { error: campaignError } = await (supabase as any)
        .from('follow_up_campaigns')
        .update({
          nome: draft.nome.trim(),
          descricao: draft.descricao.trim() || null,
          tipo: draft.tipo,
          dias_inatividade: janelaDiasCompatibilidade,
          janela_inatividade_minutos: draft.janela_inatividade_minutos,
          janela_maxima_inatividade_minutos: draft.janela_maxima_inatividade_minutos,
          horario_envio: draft.horario_envio,
          horario_limite_envio: draft.horario_limite_envio,
          modo_envio: draft.modo_envio,
          apenas_dias_uteis: draft.apenas_dias_uteis,
          tipo_gatilho: draft.tipo_gatilho,
          padrao_gatilho_texto: draft.padrao_gatilho_texto.trim() || null,
          padrao_match_type: draft.padrao_match_type,
          usa_ia: draft.usa_ia,
          ia_prompt_base: draft.ia_prompt_base.trim() || null,
          auto_pausar_ao_responder: draft.auto_pausar_ao_responder,
          apenas_leads_anuncio: draft.apenas_leads_anuncio,
          ad_name_filtro: draft.apenas_leads_anuncio ? (draft.ad_name_filtro.trim() || null) : null,
          apenas_link_personalizado: draft.apenas_link_personalizado,
          // Atualiza o filtro junto com as demais regras para que a edição não mantenha um valor antigo oculto.
          // Consequência: desligar o toggle remove a segmentação da campanha no banco.
          etapa_kanban_id_filtro: draft.filtrar_por_etapa_kanban
            ? (draft.etapa_kanban_id_filtro || null)
            : null,
          // Público bloqueado: o processor confere na criação e de novo na hora do envio.
          etapas_bloqueadas_ids: draft.etapas_bloqueadas_ids,
          ignorar_leads_com_venda: draft.ignorar_leads_com_venda,
          cooldown_dias_relancamento: draft.cooldown_dias_relancamento,
          limite_diario: draft.limite_diario,
          usar_classificacao_inteligente: draft.usar_classificacao_inteligente,
          frases_sem_followup: draft.frases_sem_followup
            .split('\n')
            .map((frase) => frase.trim())
            .filter(Boolean),
          regras_atualizadas_em: nowIso,
          updated_at: nowIso,
        })
        .eq('id', id);

      if (campaignError) {
        throw new Error(`Erro ao atualizar campanha: ${campaignError.message}`);
      }

      // Tabela: follow_up_templates
      // Operação: SELECT
      // Campos afetados: nenhum; carrega IDs atuais para atualizar sem perder histórico.
      // Filtros: campaign_id da campanha em edição.
      // Riscos: evita delete em massa que apagaria execuções por cascade.
      const { data: existingTemplates, error: existingError } = await (supabase as any)
        .from('follow_up_templates')
        .select('id')
        .eq('campaign_id', id)
        // Tabela: follow_up_templates; Operação: SELECT; Filtro: somente etapas vigentes da campanha.
        // Risco: templates já arquivados não entram novamente na edição corrente.
        .is('archived_at', null);

      if (existingError) {
        throw new Error(`Erro ao carregar etapas atuais: ${existingError.message}`);
      }

      // Mantém os IDs como string para que a desativação preserve o contrato do filtro `.in`.
      const existingIds = new Set<string>((existingTemplates ?? []).map((template: { id: string }) => template.id));
      const payloadTemplates = draft.templates
        .filter((template) => template.conteudo.trim() || template.gerado_por_ia || template.imagem_object_key)
        .map((template, index) => ({
          id: template.id ?? null,
          sequencia: index + 1,
          titulo: template.titulo.trim() || `Mensagem ${index + 1}`,
          conteudo: template.conteudo.trim() || 'Mensagem gerada por IA no momento do envio.',
          tipo_mensagem: template.imagem_object_key ? 'image' : 'text',
          intervalo_dias: index === 0 ? 0 : Math.max(1, Math.ceil(template.intervalo_minutos / 1440)),
          intervalo_minutos: index === 0 ? 0 : template.intervalo_minutos,
          ativo: true,
          gerado_por_ia: Boolean(template.gerado_por_ia),
          // Mantém a coluna legada vazia porque o modelo efetivo agora vem da configuração global de follow-up.
          // Motivo: a campanha não deve carregar uma escolha de modelo feita no modal para substituir a regra administrativa.
          ia_modelo: null,
          imagem_object_key: template.imagem_object_key || null,
          imagem_bucket: template.imagem_bucket || null,
          imagem_mimetype: template.imagem_mimetype || null,
          imagem_nome_arquivo: template.imagem_nome_arquivo || null,
          template_oficial_id: template.template_oficial_id || null,
          mapeamento_variaveis: template.mapeamento_variaveis ?? [],
        }));

      if (payloadTemplates.length === 0) {
        throw new Error('A campanha precisa manter pelo menos uma mensagem ativa.');
      }

      const keptTemplateIds = new Set<string>();

      for (const template of payloadTemplates) {
        if (template.id && existingIds.has(template.id)) {
          keptTemplateIds.add(template.id);

          // Tabela: follow_up_templates
          // Operação: UPDATE
          // Campos afetados: sequência, conteúdo e metadados de IA da etapa existente.
          // Filtros: id da etapa.
          // Riscos: preserva IDs antigos para não quebrar histórico de execuções.
          const { error: updateTemplateError } = await (supabase as any)
            .from('follow_up_templates')
            .update({
              sequencia: template.sequencia,
              titulo: template.titulo,
              conteudo: template.conteudo,
              tipo_mensagem: template.tipo_mensagem,
              intervalo_dias: template.intervalo_dias,
              intervalo_minutos: template.intervalo_minutos,
              ativo: true,
              gerado_por_ia: template.gerado_por_ia,
              // Remove o valor antigo por etapa ao editar para manter uma única fonte de configuração.
              // Motivo: o administrador controla o modelo em ai_model_settings, não na campanha.
              ia_modelo: null,
              imagem_object_key: template.imagem_object_key,
              imagem_bucket: template.imagem_bucket,
              imagem_mimetype: template.imagem_mimetype,
              imagem_nome_arquivo: template.imagem_nome_arquivo,
              template_oficial_id: template.template_oficial_id || null,
              mapeamento_variaveis: template.mapeamento_variaveis ?? [],
              updated_at: new Date().toISOString(),
            })
            .eq('id', template.id);

          if (updateTemplateError) {
            throw new Error(`Erro ao atualizar etapa: ${updateTemplateError.message}`);
          }
        } else {
          // Tabela: follow_up_templates
          // Operação: INSERT
          // Campos afetados: cria nova etapa vinculada à campanha.
          // Filtros: sem where em insert; campaign_id já validado.
          // Riscos: adiciona nova mensagem ao fluxo; criada ativa por padrão.
          const { data: insertedTemplate, error: insertTemplateError } = await (supabase as any)
            .from('follow_up_templates')
            .insert({
              campaign_id: id,
              sequencia: template.sequencia,
              titulo: template.titulo,
              conteudo: template.conteudo,
              tipo_mensagem: template.tipo_mensagem,
              intervalo_dias: template.intervalo_dias,
              intervalo_minutos: template.intervalo_minutos,
              ativo: true,
              gerado_por_ia: template.gerado_por_ia,
              // Mantém a coluna legada vazia também para novas etapas adicionadas à campanha.
              // Motivo: evita reintroduzir configuração de modelo no fluxo do usuário da clínica.
              ia_modelo: null,
              imagem_object_key: template.imagem_object_key,
              imagem_bucket: template.imagem_bucket,
              imagem_mimetype: template.imagem_mimetype,
              imagem_nome_arquivo: template.imagem_nome_arquivo,
              template_oficial_id: template.template_oficial_id || null,
              mapeamento_variaveis: template.mapeamento_variaveis ?? [],
            })
            .select('id')
            .single();

          if (insertTemplateError) {
            throw new Error(`Erro ao criar etapa nova: ${insertTemplateError.message}`);
          }

          if (insertedTemplate?.id) {
            keptTemplateIds.add(insertedTemplate.id);
          }
        }
      }

      const templateIdsToDisable = Array.from(existingIds).filter((templateId) => !keptTemplateIds.has(templateId));

      if (templateIdsToDisable.length > 0) {
        // Tabela: follow_up_templates
        // Operação: UPDATE
        // Campos afetados: ativo=false para etapas removidas no wizard.
        // Filtros: IDs que existiam antes e não vieram no payload da edição.
        // Riscos: não apaga histórico; remove etapa do fluxo futuro.
        const { error: disableTemplateError } = await (supabase as any)
          .from('follow_up_templates')
          .update({ ativo: false, updated_at: new Date().toISOString() })
          .in('id', templateIdsToDisable);

        if (disableTemplateError) {
          throw new Error(`Erro ao desativar etapa removida: ${disableTemplateError.message}`);
        }

        // Tabela: follow_up_execucoes
        // Operação: UPDATE
        // Campos afetados: status e updated_at de execuções pendentes de etapas removidas.
        // Filtros: template_id em etapas desativadas e status pendente.
        // Riscos: evita envio de conteúdo que foi removido na edição da campanha.
        const { error: cancelPendingError } = await (supabase as any)
          .from('follow_up_execucoes')
          .update({
            status: 'cancelado',
            erro_detalhes: 'Execução cancelada porque a etapa foi removida na edição da campanha.',
            updated_at: new Date().toISOString(),
          })
          .in('template_id', templateIdsToDisable)
          .eq('status', 'pendente');

        if (cancelPendingError) {
          throw new Error(`Erro ao cancelar execuções pendentes: ${cancelPendingError.message}`);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followup-campaigns'] });
      toast.success('Campanha atualizada com sucesso.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useUpdateFollowupCampaignStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ativo }: { id: string; ativo: boolean }) => {
      const nowIso = new Date().toISOString();
      const updatePayload: Record<string, unknown> = {
        ativo,
        updated_at: nowIso,
      };

      // Reinicia o marco quando a campanha e ativada para nao processar conversas antigas.
      // Motivo: ativar uma campanha curta deve valer dali para frente, nao para todo o historico.
      if (ativo) {
        updatePayload.ativada_em = nowIso;
      }

      // Tabela: follow_up_campaigns
      // Operação: UPDATE
      // Campos afetados: ativo, updated_at e ativada_em quando a campanha e ligada.
      // Filtros: id da campanha; RLS garante que pertence à clínica.
      // Riscos: ativar campanha inicia disparos automáticos no próximo processor.
      const { error } = await (supabase as any)
        .from('follow_up_campaigns')
        .update(updatePayload)
        .eq('id', id);

      if (error) {
        throw new Error(`Erro ao atualizar campanha: ${error.message}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followup-campaigns'] });
      toast.success('Status da campanha atualizado.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useDeleteFollowupCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      // Tabela: follow_up_campaigns
      // Operação: DELETE
      // Campos afetados: remove campanha e templates/execuções por cascade do banco.
      // Filtros: id da campanha; RLS impede exclusão de outra clínica.
      // Riscos: operação destrutiva; a UI pede confirmação antes de chamar este hook.
      const { error } = await (supabase as any)
        .from('follow_up_campaigns')
        .delete()
        .eq('id', id);

      if (error) {
        const details = [error.message, error.details, error.hint]
          .filter(Boolean)
          .join(' | ');
        throw new Error(`Erro ao remover campanha: ${details}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followup-campaigns'] });
      toast.success('Campanha removida.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};

export const useLeadFollowupExecutions = (leadId: string | null) => {
  return useQuery({
    queryKey: ['followup-executions', leadId],
    enabled: !!leadId,
    queryFn: async (): Promise<FollowupExecution[]> => {
      if (!leadId) return [];

      // Tabela: follow_up_execucoes
      // Operação: SELECT
      // Campos afetados: nenhum; lista histórico/agendamentos do lead.
      // Filtros: lead_id aberto pelo usuário.
      // Riscos: expõe status de automações; RLS limita aos leads da clínica.
      const { data, error } = await (supabase as any)
        .from('follow_up_execucoes')
        .select('*, follow_up_campaigns(nome), follow_up_templates(titulo, conteudo, sequencia)')
        .eq('lead_id', leadId)
        .order('data_agendada', { ascending: true });

      if (error) {
        throw new Error(`Erro ao buscar execuções: ${error.message}`);
      }

      return (data ?? []) as FollowupExecution[];
    },
    staleTime: 30 * 1000,
  });
};

export const useToggleLeadFollowup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ leadId, pausado }: { leadId: string; pausado: boolean }) => {
      // Tabela: leads
      // Operação: UPDATE
      // Campos afetados: follow_up_pausado e updated_at.
      // Filtros: id do lead selecionado.
      // Riscos: pausa/retoma automações para o lead; ação explícita do atendente.
      const { error } = await (supabase as any)
        .from('leads')
        .update({
          follow_up_pausado: pausado,
          updated_at: new Date().toISOString(),
        })
        .eq('id', leadId);

      if (error) {
        throw new Error(`Erro ao alterar follow-up: ${error.message}`);
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      queryClient.invalidateQueries({ queryKey: ['lead-followup-status', variables.leadId] });
      toast.success(variables.pausado ? 'Follow-up pausado para este lead.' : 'Follow-up reativado para este lead.');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
};
